/* eslint-disable react/display-name */
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  GridColDef,
  GridRowsProp,
  GridFilterModel,
  GridSortModel,
  GridRenderCellParams,
  GridCellEditCommitParams,
  GridSelectionModel,
  GridRowScrollEndParams,
  GridRowModel,
  useGridApiRef,
  GridRowParams,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid-pro';
import { useGetGroups, useRenameDevice, useLoadInfiniteDevices } from '@bymason/controller-api-hooks';
import { useAuth } from '@bymason-private/auth-provider';
import { useRouter } from '@bymason-private/route-provider';
import { ControllerDataGrid, dataGridLinkRendererFactory } from '@bymason-private/legacy-pattern-components';
import {
  dataGridLongDateTimeFormatter,
  DataGridHightlightSearchQueryCellRender,
  DataGridEditableCellRender,
  DataGridColumnsPanel,
} from '@bymason-private/pattern-components';

import { useToast } from '../../providers';
import { DEFAULT_BOTTOM_SCROLL_THRESHOLD, DEFAULT_GROUPS_QUERY_LIMIT } from '../../app-constants';
import {
  getBrowserStorageKey,
  persistGridColumnState,
  persistGridColumnVisibility,
  retreiveGridColumnVisibility,
  retreiveGridDensity,
} from '../../browser-storage';
import {
  booleanOperator,
  deviceListOptionsReducer,
  DevicesListActionTypes,
  getOptionsFilterModelSortModelFromUrl,
  groupOperator,
  searchOperator,
} from './logic';
import { DevicesFilterPanel } from './device-filter-panel';
import { DevicesToolbar } from './devices-toolbar';
import { PercentMeter } from './percent-meter';
import { UpdatedAlert } from '../updated-alert';

export { GridRowParams as DeviceRow } from '@mui/x-data-grid-pro';

interface GroupValueOptions {
  value: string;
  label: string;
}

interface DevicesListProps {
  showDetails?: string;
  topOffsetPx?: number;
  noDataMessage?: React.ReactChild;
  onClickUuidLink?: (params: GridRenderCellParams) => void;
  onRowClick?: (params: GridRowParams) => void;
  onRowDoubleClick?: (params: GridRowParams) => void;
}

export function DevicesList({
  showDetails,
  topOffsetPx = 0,
  noDataMessage,
  onClickUuidLink,
  onRowClick,
  onRowDoubleClick,
}: DevicesListProps) {
  const { currentURL } = useRouter();
  const { authToken = '' } = useAuth();
  const apiRef = useGridApiRef();
  const [setToastMessage, setToastSeverity] = useToast();

  // caclulate initial filter/sort states and initial options from search params
  const { sortModel, filterModel, options: initialOptions } = getOptionsFilterModelSortModelFromUrl(
    currentURL
  );

  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const lastSelectionModel = useRef<GridSelectionModel>([]);
  const lastRowStateBeforeEdit = useRef<GridRowModel | null>(null);
  const [options, dispatchOptions] = useReducer(deviceListOptionsReducer, initialOptions);
  const [refreshingData, setRefreshingData] = useState(false);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    ...retreiveGridColumnVisibility(),
    all: false,
  });
  const [devices, devicesError, devicesBusy] = useLoadInfiniteDevices(authToken, options, isScrolledToBottom);
  const [renameDevice, renameResult, renameError, renameBusy] = useRenameDevice(authToken);
  const [getGroups, groups] = useGetGroups(authToken);

  const groupValues = useMemo<GroupValueOptions[]>(() => {
    return groups?.data?.map((g) => ({ value: g.name, label: g.name })) || [];
  }, [groups]);

  const gridColumns = useMemo<GridColDef[]>(() => {
    const defaultColDef: GridColDef[] = [
      {
        field: 'id',
        headerName: 'UUID',
        filterable: false,
        sortable: false,
        type: 'string',
        renderCell:
          onClickUuidLink &&
          dataGridLinkRendererFactory((params) => {
            onClickUuidLink(params);
          }),
        width: 300,
      },
      {
        field: 'serial',
        headerName: 'Serial',
        filterable: true,
        sortable: false,
        flex: 3,
        type: 'string',
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => <DataGridHightlightSearchQueryCellRender {...params} />,
        filterOperators: searchOperator,
      },
      {
        field: 'imei',
        headerName: 'IMEI',
        filterable: true,
        sortable: false,
        flex: 3,
        type: 'string',
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => <DataGridHightlightSearchQueryCellRender {...params} />,
        filterOperators: searchOperator,
      },
      {
        field: 'name',
        headerName: 'Name',
        filterable: true,
        sortable: false,
        editable: true,
        flex: 1,
        type: 'string',
        minWidth: 150,
        renderCell: (params: GridRenderCellParams) => (
          <DataGridEditableCellRender {...params}>
            <DataGridHightlightSearchQueryCellRender {...params} />
          </DataGridEditableCellRender>
        ),
        filterOperators: searchOperator,
      },
      {
        field: 'osUpdated',
        headerName: 'OS Status',
        align: 'left',
        headerAlign: 'left',
        filterable: true,
        sortable: false,
        type: 'boolean',
        width: 150,
        filterOperators: booleanOperator,
        renderCell: (params: GridRenderCellParams) => <UpdatedAlert value={params.value} />,
      },
      {
        field: 'projectUpdated',
        align: 'left',
        headerAlign: 'left',
        headerName: 'Project Status',
        sortable: false,
        type: 'boolean',
        width: 150,
        filterOperators: booleanOperator,
        renderCell: (params: GridRenderCellParams) => <UpdatedAlert value={params.value} />,
      },
      {
        field: 'appsUpdated',
        headerName: 'Apps Status',
        align: 'left',
        headerAlign: 'left',
        filterable: true,
        sortable: false,
        type: 'boolean',
        width: 150,
        filterOperators: booleanOperator,
        renderCell: (params: GridRenderCellParams) => <UpdatedAlert value={params.value} />,
      },
      {
        field: 'deviceGroup',
        headerName: 'Group',
        filterable: true,
        sortable: false,
        flex: 1,
        type: 'singleSelect',
        valueOptions: groupValues,
        minWidth: 150,
        filterOperators: groupOperator,
      },
      {
        field: 'model',
        headerName: 'Model',
        filterable: false,
        sortable: false,
        type: 'string',
      },
      {
        field: 'battery',
        headerName: 'Battery',
        filterable: true,
        sortable: false,
        type: 'number',
        headerAlign: 'center',
        renderCell: (params: GridRenderCellParams) => <PercentMeter valuePercent={params.value} />,
      },
      {
        field: 'lastHeartbeat',
        headerName: 'Last check in',
        filterable: false,
        sortable: true,
        type: 'date',
        valueFormatter: dataGridLongDateTimeFormatter,
        minWidth: 280,
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        filterable: false,
        sortable: true,
        type: 'date',
        valueFormatter: dataGridLongDateTimeFormatter,
        minWidth: 280,
      },
      {
        // hidden combined field with no data for the sake of filtering
        field: 'all',
        headerName: 'Serial/IMEI/Name',
        disableReorder: true,
        filterOperators: searchOperator,
        filterable: true,
        sortable: false,
      },
    ];

    return defaultColDef;
  }, [groupValues, onClickUuidLink]);

  const gridRows = useMemo<GridRowsProp>(() => {
    return devices
      ? devices.map((record) => ({
          id: record.id,
          serial: record.serialnumber,
          imei: record.imei,
          name: record.name,
          deviceGroup: record.group?.name,
          model: record.family,
          osUpdated: record.status.osUpdated,
          appsUpdated: record.status.appsUpdated,
          projectUpdated: record.status.projectUpdated,
          battery: record.battery.level,
          lastHeartbeat: record.lastcheckin,
          createdAt: record.createdAt,
        }))
      : [];
  }, [devices]);

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setRefreshingData(true);
    dispatchOptions({ filterPayload: newFilterModel, type: DevicesListActionTypes.Filter });
  };

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setRefreshingData(true);
    dispatchOptions({ sortPayload: newSortModel, type: DevicesListActionTypes.Sort });
  };

  const handleColumnsOrderChange = () => {
    persistGridColumnState(apiRef.current.getAllColumns());
  };

  const handleColumnsResize = () => {
    persistGridColumnState(apiRef.current.getAllColumns());
  };

  const handleSelectionModelChange = (newSelections: GridSelectionModel) => {
    setSelectionModel(newSelections);
    lastSelectionModel.current = newSelections;
  };

  const handleRowsScrollEnd = (params: GridRowScrollEndParams) => {
    setIsScrolledToBottom(params.virtualRowsCount > 0);
  };

  const handleEditCellCommit = useCallback(
    (params: GridCellEditCommitParams) => {
      const { field, id, value } = params;
      const prevCellValue = apiRef.current.getCellValue(id, field);
      lastRowStateBeforeEdit.current = apiRef.current.getRow(id) || null;

      if (prevCellValue !== value) {
        if (field === 'name' && typeof value === 'string' && id) {
          renameDevice({ name: String(value), deviceId: String(id) });
        }
      }
    },
    [apiRef, renameDevice]
  );

  const handleColumnVisibilityChange = useCallback((newModel: GridColumnVisibilityModel) => {
    const visModel = { ...newModel, all: false };
    persistGridColumnVisibility(visModel);
    setColumnVisibilityModel(visModel);
  }, []);

  useEffect(() => {
    getGroups({ limit: DEFAULT_GROUPS_QUERY_LIMIT });
  }, [getGroups]);

  useEffect(() => {
    setRefreshingData(false);
  }, [devices]);

  useEffect(() => {
    // These effects should only run when the devices length changes
    setSelectionModel(lastSelectionModel.current);
    setIsScrolledToBottom(!devices.length);
  }, [devices.length]);

  useEffect(() => {
    // TODO: Shouldn't constructor arg typings be Record<string, string | number | boolean>?
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const queryParams: Record<string, any> = showDetails ? { ...options, showDetails } : options;
    const search = new URLSearchParams(queryParams);
    search.delete('limit');
    const newUrl = search.toString() ? `${currentURL.pathname}?${search.toString()}` : currentURL.pathname;
    history?.replaceState(null, '', newUrl);

    // reload last selections whenever options changes
    // force useLoadInfiniteDevices to load new data
    setSelectionModel(lastSelectionModel.current);
  }, [options, showDetails, currentURL]);

  useEffect(() => {
    if (!renameBusy && renameResult && !renameError) {
      setToastSeverity('success');
      setToastMessage(`Your device ${renameResult.id} has been successfully renamed!`);
    }
  }, [renameBusy, renameError, renameResult, setToastMessage, setToastSeverity]);

  useEffect(() => {
    const errorMessage = devicesError || renameError;

    if (errorMessage) {
      setToastSeverity('error');
      setToastMessage(errorMessage);
    }
    if (renameError && lastRowStateBeforeEdit.current) {
      apiRef.current.updateRows([lastRowStateBeforeEdit.current]);
      apiRef.current.setCellMode(lastRowStateBeforeEdit.current.id, 'name', 'edit');
    }
  }, [apiRef, devicesError, renameError, setToastMessage, setToastSeverity]);

  const showZeroState = useMemo(() => {
    // this line suppresses a Type error that is only seen in local dev
    // if (process.env.NODE_ENV === 'development') apiRef.current = {} as GridApi;

    return (
      !apiRef.current &&
      !devices.length &&
      !devicesBusy &&
      !refreshingData &&
      Object.keys(options).length <= 1
    );
  }, [apiRef, devices.length, devicesBusy, options, refreshingData]);

  return (
    <Box component='section' width='100%' height={`calc(100vh - ${topOffsetPx}px)`}>
      {showZeroState ? (
        noDataMessage
      ) : (
        <ControllerDataGrid
          apiRef={showZeroState ? undefined : apiRef}
          sx={{
            '& .MuiDataGrid-row:hover': onRowClick
              ? {
                  cursor: 'pointer',
                }
              : {},
            '& .MuiDataGrid-virtualScroller': {
              pb: 8,
            },
          }}
          initialState={{
            filter: { filterModel },
            sorting: { sortModel },
          }}
          components={{
            ColumnsPanel: DataGridColumnsPanel,
            Toolbar: DevicesToolbar,
            FilterPanel: DevicesFilterPanel,
          }}
          componentsProps={{
            columnsPanel: {
              suppressFields: ['all'],
              resetColumnStateKey: getBrowserStorageKey('grid-column-visibility-state'),
            },
          }}
          autoHeight={false}
          rows={gridRows}
          columns={gridColumns}
          checkboxSelection
          density={retreiveGridDensity() || 'comfortable'}
          disableSelectionOnClick
          disableColumnReorder={false}
          disableColumnResize={false}
          disableColumnSelector={false}
          disableDensitySelector={false}
          disableMultipleColumnsSorting
          getRowClassName={(params) => {
            return params.id === showDetails ? 'active-row' : '';
          }}
          loading={devicesBusy || refreshingData}
          scrollEndThreshold={DEFAULT_BOTTOM_SCROLL_THRESHOLD}
          selectionModel={selectionModel}
          sortingMode='server'
          filterMode='server'
          onColumnOrderChange={handleColumnsOrderChange}
          onColumnResize={handleColumnsResize}
          onCellEditCommit={handleEditCellCommit}
          onFilterModelChange={handleFilterModelChange}
          onRowClick={onRowClick}
          onRowDoubleClick={onRowDoubleClick}
          onRowsScrollEnd={handleRowsScrollEnd}
          onSelectionModelChange={handleSelectionModelChange}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
        />
      )}
    </Box>
  );
}
